// 4-step Diamond Verification wizard.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, X, ChevronRight, FlaskConical, Gem } from 'lucide-react';
import { toast } from 'sonner';
import { API, token, LABS, LAB_COLORS, STEP_TITLES, COLORS, CLARITIES } from './constants';
import { Section, NumInput, ColorPicker, ClarityPicker, StepDots } from './common';
import { Step2PillFlow } from './Step2PillFlow';
import { PhotoSection } from './PhotoSection';

export const Wizard = ({ tag, onClose, onSaved }) => {
  const navigate = useNavigate();
  const [step, setStep]   = useState(tag?.current_step || 1);
  const [saving, setSaving] = useState(false);

  // ── Revision mode ─────────────────────────────────────────────────────
  // When the wizard opens with a *completed* DV, we treat this run as a
  // revision. Business rule (Feb-2026): the entire form starts EMPTY except
  // for the tag number, forcing a fresh ERP fetch. This way the team is not
  // biased by the old snapshot — they re-verify against today's physical
  // reality (weights, stones, grades, photos all re-entered from scratch).
  const isRevision = !!(tag?.id && tag?.status === 'complete');
  const initTag = isRevision
    ? { id: tag.id, tag_number: tag.tag_number, status: tag.status }
    : tag;

  // No-drafts: the DV record exists ONLY after the final atomic submit.
  // We hold every state purely in React until Step 4's Save & Complete.

  // Step 1
  const [tagNumber,    setTagNumber]    = useState(initTag?.tag_number || '');
  const [jewelleryRef, setJewelleryRef] = useState(initTag?.jewellery_ref || '');
  const [productOptions, setProductOptions] = useState([]);

  // Load product masters for dropdown
  useEffect(() => {
    axios.get(`${API}/api/catalog/products`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => setProductOptions((res.data || []).map(p => p.name).filter(Boolean).sort()))
      .catch(() => {});
  }, []);

  // Revision mode: as soon as the wizard opens, force-fetch the current ERP
  // truth for this tag_number so the team is never biased by the old snapshot.
  // Runs exactly once on mount; the fetchErpPills call resets stone entries
  // and grossWt from the ERP response.
  const bootedErpRef = useRef(false);

  // Step 2 – actual diamond entries
  const divertedSet = new Set((initTag?.diverted_pills || []).map(Number));
  const [diamondEntries, setDiamondEntries] = useState(() => {
    const ent = initTag?.actual?.entries;
    if (ent?.length) {
      return ent.map(e => ({
        id: Math.random(),
        shape: e.shape || '',
        colors: e.colors || [],
        clarities: e.clarities || [],
        weightCts: e.weight_cts ?? '',
        count: e.count ?? '',
        size: e.size || '',
        stoneCode: e.stone_code || '',
        serialNo: e.serial_no ?? null,
        isWeightLocked: !!e.is_weight_locked,
        weightOverridden: !!e.weight_overridden,
        erpSource: e.erp_source || null,
        actionable: e.actionable !== false,
        isSilent: !!e.is_silent,
        isDiamond: e.is_diamond !== false,
        completed: !!e.completed,
        divertedToCsv: e.serial_no != null && divertedSet.has(Number(e.serial_no)),
      }));
    }
    // Backward compat: old flat format
    if (initTag?.actual?.colors?.length || initTag?.actual?.clarities?.length) {
      return [{
        id: Math.random(),
        shape: '',
        colors: initTag.actual.colors || [],
        clarities: initTag.actual.clarities || [],
        weightCts: initTag.actual.total_weight_cts ?? '',
        count: initTag.actual.diamond_count ?? '',
        size: '',
        stoneCode: '',
        serialNo: null,
        isWeightLocked: false,
        weightOverridden: false,
        erpSource: null,
        actionable: true,
        isSilent: false,
        isDiamond: true,
        completed: false,
      }];
    }
    return [];
  });
  const [grossWt, setGrossWt] = useState(initTag?.actual?.gross_weight ?? '');
  const [dataSource, setDataSource] = useState(initTag?.actual?.data_source || 'manual');

  // Step 4 – product photos
  const [photos, setPhotos] = useState(() => initTag?.photos || []);

  // Step 2 – ERP pill-strip state
  const [erpFetching, setErpFetching] = useState(false);
  const [erpError, setErpError]       = useState('');
  const [erpTagMeta, setErpTagMeta]   = useState(initTag?.actual?.erp_tag_meta || null);
  const [activePillIdx, setActivePillIdx] = useState(null);
  const [lastFetchedTag, setLastFetchedTag] = useState(initTag?.actual?.erp_tag_meta?.tag_id || '');
  const [step1Prefilled, setStep1Prefilled] = useState(false);

  // Tracks last grossWt + jewelleryRef written by ERP prefill.
  const lastErpPrefill = useRef({ gross: '', product: '' });

  // Fuzzy-match ERP style_name → DiaForge catalog product option.
  const matchProductOption = useCallback((styleName) => {
    const s = (styleName || '').toUpperCase();
    if (!s || !productOptions.length) return '';
    let best = '';
    let bestLen = 0;
    for (const opt of productOptions) {
      const optUp = opt.toUpperCase();
      const singular = optUp.replace(/(IES|ES|S)$/, '');
      const tokens = new Set([optUp, singular].filter(Boolean));
      for (const t of tokens) {
        if (t.length >= 3 && s.includes(t) && t.length > bestLen) {
          best = opt;
          bestLen = t.length;
        }
      }
    }
    return best;
  }, [productOptions]);

  // Fetch ERP data for the typed tag number and rebuild pills.
  const fetchErpPills = useCallback(async (tagToFetch) => {
    const t = String(tagToFetch || '').trim();
    if (!t) {
      setErpError('Enter a Tag Number on Step 1 first.');
      return;
    }
    setErpFetching(true);
    setErpError('');
    try {
      const res = await axios.get(`${API}/api/erp/diamond-verification/tag/${encodeURIComponent(t)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = res.data;
      setErpTagMeta({ ...data.tag_meta, tag_id: data.tag_id, source: data.source });
      setLastFetchedTag(t);

      // Step 1 prefill — overwrite only if field is empty, OR equals last ERP value.
      const meta = data.tag_meta || {};
      const newGross = meta.gross_wt ? String(meta.gross_wt) : '';
      if (newGross && (!grossWt || grossWt === lastErpPrefill.current.gross)) {
        setGrossWt(newGross);
      }
      const matched = matchProductOption(meta.style_name || meta.combination_name || '');
      if (matched && (!jewelleryRef || jewelleryRef === lastErpPrefill.current.product)) {
        setJewelleryRef(matched);
      }
      lastErpPrefill.current = { gross: newGross, product: matched };
      setStep1Prefilled(true);

      // Convert pills → diamondEntries
      const newEntries = (data.pills || []).map((p, idx) => {
        // ── P1: Stone-code decoder auto-fill ─────────────────────────────
        // The backend has already decoded the stone_code via the 4-position
        // rule. If `decoded` is present, the pill's Shape/Colour/Clarity/Size
        // are set directly and the pill is locked + auto-marked complete.
        // ── P2: Routing verdict ─────────────────────────────────────────
        // Backend also tags every is_diamond pill with a routing verdict
        // (dv_auto / csv_standard / csv_premium). Rejected pills caused the
        // whole fetch to 400 earlier so they can never reach here.
        const d = p.decoded || null;
        const routing = p.routing || null;
        const isCsvRoute = !!routing && (routing.route === 'csv_standard' || routing.route === 'csv_premium');
        // Feb-2026: MIX-clarity pills (`d.auto_csv === true`) now also
        // auto-fill in DV — the pricing engine folds MIX → VS of the same
        // colour. Previously they diverted to CSV.
        const autoFilled = !!d && !isCsvRoute;
        const isMixSubstitution = !!d && !!d.auto_csv && !isCsvRoute;
        return {
          id: `erp-${data.tag_id}-${p.serial_no || idx}`,
          shape:      autoFilled ? d.shape       : '',
          colors:     autoFilled ? [...d.colours] : [],
          clarities:  autoFilled ? [d.clarity]    : [],
          size:       autoFilled ? d.sizes[0]     : '',
          // Mar-2026: the decoder now always returns exactly ONE Fancy size
          // chip per letter (M/O/P → "-7", N/Q → "+7"). We keep sizesLocked
          // as a single-element list for backward-compat with any UI that
          // still iterates it — but there will only ever be one chip inside.
          sizesLocked: autoFilled ? [...d.sizes]  : null,
          weightCts:  p.weight_cts ?? '',
          count:      p.pieces ?? '',
          stoneCode:  p.stone_code || '',
          serialNo:   p.serial_no ?? null,
          isWeightLocked:    true,
          weightOverridden:  false,
          erpSource:  data.source,
          // Auto-filled pills are still real diamond entries — they appear in
          // the pill strip as completed (emerald). They're just non-editable.
          // CSV-routed pills appear as silent with a "→ CSV" badge.
          actionable: !!p.actionable && !isCsvRoute,
          isSilent:   !p.actionable || isCsvRoute,
          isDiamond:  !!p.is_diamond,
          completed:  autoFilled,                       // auto-filled = done
          locked:     autoFilled,                       // P1: lock the editor
          decoded:    d,                                // raw decoder payload (for UI badges)
          isMixSubstitution: isMixSubstitution,        // Feb-2026: MIX → priced as VS chip
          // P2: pending auto-CSV divert. We mark the pill visually now and
          // fire the actual /divert-to-csv call once we have a createdTagId.
          pendingAutoCsv: isCsvRoute,
          autoCsvPricingMode: isCsvRoute ? (routing.pricing_mode || 'standard') : null,
          autoCsvReason:      isCsvRoute ? routing.reason : null,
          // No-drafts: auto-route pills are diverted purely client-side. Their
          // serial_no will travel in the final /submit payload's diverted_pills.
          divertedToCsv:      isCsvRoute,
        };
      });
      setDiamondEntries(newEntries);
      setDataSource('erp');
      const firstActionable = newEntries.findIndex(e => e.actionable && !e.completed);
      setActivePillIdx(firstActionable >= 0 ? firstActionable : null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'ERP lookup failed.';
      setErpError(msg);
      setErpTagMeta(null);
      // Clear stale ERP-prefilled values, preserving any user edits.
      if (grossWt && grossWt === lastErpPrefill.current.gross) setGrossWt('');
      if (jewelleryRef && jewelleryRef === lastErpPrefill.current.product) setJewelleryRef('');
      lastErpPrefill.current = { gross: '', product: '' };
    } finally {
      setErpFetching(false);
    }
  }, [matchProductOption, grossWt, jewelleryRef]);

  // Revision-mode auto-fetch: kick the ERP fetch once, on mount, so the team
  // never opens the wizard staring at an empty Step 2. Uses a ref-guard so
  // React StrictMode's double-invocation doesn't fire twice.
  useEffect(() => {
    if (isRevision && tagNumber && !bootedErpRef.current) {
      bootedErpRef.current = true;
      fetchErpPills(tagNumber);
    }
  }, [isRevision, tagNumber, fetchErpPills]);

  // ── Option C — Zero-diamond / zero-stone tag deflection ─────────────────
  // If the ERP fetch returns:
  //   • zero stones at all (plain gold tag), OR
  //   • only non-diamond stones (colour-only),
  // the tag can't go through DV. We offer a one-click handoff to create a
  // CSV draft directly via /from-erp-tag and navigate the user.
  const [deflecting, setDeflecting] = useState(false);
  const noDiamondTag = (
    dataSource === 'erp' && !!erpTagMeta && (
      diamondEntries.length === 0 ||
      diamondEntries.every(e => e.actionable === false && e.isDiamond === false)
    )
  );

  // Hard rejection — tag contains stone codes outside the supported DV ruleset.
  // The Step 1 "Save & Next" must be blocked entirely: no manual override.
  const tagRejected = (
    !!erpError && /outside the supported diamond verification ruleset/i.test(erpError)
  );

  // Non-HO tag — ERP lookup returned 404. Block Save & Next; only Back is allowed.
  const tagNotAtHo = (
    !!erpError && !tagRejected && /not currently at ho|is not currently at ho/i.test(erpError)
  );

  const deflectToCsv = useCallback(async () => {
    if (!tagNumber) {
      toast.error('Enter a tag number first');
      return;
    }
    setDeflecting(true);
    try {
      const res = await axios.post(
        `${API}/api/colour-stone-verification/from-erp-tag`,
        { tag_number: tagNumber.trim() },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Tag ${tagNumber} — sent to Colour Stone Verification`);
      onSaved?.();
      onClose?.();
      navigate(`/colour-stone-verification/${res.data.csv_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send to CSV');
    } finally {
      setDeflecting(false);
    }
  }, [tagNumber, navigate, onClose, onSaved]);

  // ── Per-pill diversion to Colour Stone Verification (local-only) ────────
  // No-drafts: diversion is purely a client-side flag. The pill's serial_no
  // is collected and sent in the final /submit payload (`diverted_pills`).
  const divertPillToCsv = useCallback((entry) => {
    if (entry?.serialNo == null) {
      toast.error('This pill has no ERP serial number — divert is only available for ERP-loaded pills.');
      return;
    }
    if (!window.confirm(
      `Move stone ${entry.stoneCode || 'S' + entry.serialNo} ` +
      `(${Number(entry.weightCts || 0).toFixed(3)} ct, ${entry.count || 0} pc) ` +
      `to Colour Stone Verification?`
    )) return;
    setDiamondEntries(prev => prev.map(e =>
      (e.serialNo === entry.serialNo)
        ? { ...e, divertedToCsv: true, actionable: false, isSilent: true }
        : e
    ));
    setActivePillIdx(prev => {
      if (prev == null) return prev;
      const nextActive = diamondEntries.findIndex((e, i) =>
        i !== prev && e.actionable && !e.completed && !e.divertedToCsv &&
        e.serialNo !== entry.serialNo
      );
      return nextActive >= 0 ? nextActive : null;
    });
  }, [diamondEntries]);

  // Bulk diversion (multi-select) — also purely local now.
  const bulkDivertPillsToCsv = useCallback((selectedEntries) => {
    const targetSerials = new Set(
      (selectedEntries || [])
        .filter(e => e?.serialNo != null && !e.divertedToCsv)
        .map(e => Number(e.serialNo)),
    );
    if (targetSerials.size === 0) return;
    setDiamondEntries(prev => prev.map(e =>
      targetSerials.has(Number(e.serialNo))
        ? { ...e, divertedToCsv: true, actionable: false, isSilent: true }
        : e
    ));
  }, []);

  // Step 1 input handler — clears stale ERP state on edit.
  const handleTagNumberChange = useCallback((val) => {
    setTagNumber(val);
    setErpError('');
    setErpTagMeta(null);
    setStep1Prefilled(false);
  }, []);

  // Step 3 – tds
  const [tdsColors,    setTdsColors]    = useState(initTag?.tds?.colors || []);
  const [tdsClarities, setTdsClarities] = useState(initTag?.tds?.clarities || []);

  // Step 4 – lab proposals
  const [proposals, setProposals] = useState(initTag?.lab_proposals || []);
  const [addingLab,  setAddingLab]  = useState(false);

  // Computed totals — count ONLY actionable (diamond) entries
  const totalWeight = diamondEntries
    .filter(e => e.actionable !== false && !e.isSilent)
    .reduce((s, e) => s + (parseFloat(e.weightCts) || 0), 0);
  const totalPieces = diamondEntries
    .filter(e => e.actionable !== false && !e.isSilent)
    .reduce((s, e) => s + (parseInt(e.count) || 0), 0);

  // ── Step 2 completion gate ───────────────────────────────────────────────
  const isPillFilled = (e) =>
    !!e.shape && (e.colors || []).length > 0 && (e.clarities || []).length > 0 &&
    !!e.size && !!String(e.weightCts ?? '').trim() && !!String(e.count ?? '').trim();
  const step2ActionableTotal = diamondEntries.filter(e => e.actionable && !e.divertedToCsv).length;
  const step2ActionableDone  = diamondEntries.filter(e =>
    e.actionable && !e.divertedToCsv && e.completed && isPillFilled(e)
  ).length;
  const step2AllDone = step2ActionableTotal > 0 && step2ActionableDone === step2ActionableTotal;

  // ── P4: clamp Step 3/4 chips by the worst-actual Step 2 grade ────────────
  // Aggregate every colour & clarity selected across actionable (non-silent,
  // non-diverted) Step 2 pills, then take the worst (highest index in the
  // ordered constants). Anything past that index in Step 3/4 is disabled.
  const step2ActiveEntries = diamondEntries.filter(
    e => e.actionable && !e.divertedToCsv && !e.isSilent
  );
  const allActualColours = step2ActiveEntries.flatMap(e => e.colors || []);
  const allActualClarities = step2ActiveEntries.flatMap(e => e.clarities || []);
  const worstActualColour = allActualColours.length === 0 ? null :
    allActualColours.reduce((worst, c) =>
      COLORS.indexOf(c) > COLORS.indexOf(worst) ? c : worst, allActualColours[0]);
  const worstActualClarity = allActualClarities.length === 0 ? null :
    allActualClarities.reduce((worst, c) =>
      CLARITIES.indexOf(c) > CLARITIES.indexOf(worst) ? c : worst, allActualClarities[0]);

  // ── Step 3 completion gate ───────────────────────────────────────────────
  const step3Valid = tdsColors.length > 0 && tdsClarities.length > 0;

  // ── Step 4 completion gate ───────────────────────────────────────────────
  // Choice C: TDS proposal is always mandatory and must have colour+clarity.
  // Other added labs (GIA / IGI / HRD / SGL) are optional but, if added, must
  // also have colour+clarity.
  const tdsProposal = proposals.find(p => p.lab === 'TDS');
  const incompleteProposals = proposals.filter(
    p => p.colors.length === 0 || p.clarities.length === 0
  );
  const step4Valid =
    photos.length > 0 &&
    !!tdsProposal &&
    incompleteProposals.length === 0;
  // A batch-requiring lab (GIA/IGI) proposed → "Submit Later (Sell at TDS)"
  // becomes available: park the lab step and sell at TDS meanwhile.
  const hasLabBatch = proposals.some(p => ['GIA', 'IGI'].includes(p.lab));
  const step4MissingLabel = (() => {
    if (photos.length === 0) return 'Add a photo to complete';
    if (!tdsProposal) return 'Add the mandatory TDS lab proposal';
    if (incompleteProposals.length > 0) {
      return `Pick colour & clarity for: ${incompleteProposals.map(p => p.lab).join(', ')}`;
    }
    return 'Save & Complete';
  })();

  // Auto-add an empty TDS proposal the first time the user lands on Step 4 so
  // the requirement is self-documenting (a card appears prompting them to pick).
  useEffect(() => {
    if (step === 4 && !proposals.find(p => p.lab === 'TDS')) {
      setProposals(prev => [{ lab: 'TDS', colors: [], clarities: [] }, ...prev]);
    }
  }, [step, proposals]);

  // ── Atomic submit (Step 4 only) ─────────────────────────────────────────
  // No-drafts: this is the SOLE network write of the wizard. All Step 1-3
  // "Save & Next" buttons are pure client-side navigation.
  //
  // Revision mode (see top of file): `parent_dv_id` in the payload tells the
  // backend to bypass tag_number uniqueness and mark the parent as
  // `superseded` upon successful insert.
  const submitComplete = useCallback(async (sellMode = null) => {
    setSaving(true);
    try {
      const payload = {
        tag_number:    tagNumber || undefined,
        jewellery_ref: jewelleryRef,
        actual: {
          entries: diamondEntries.map(e => ({
            shape: e.shape || null,
            colors: e.colors,
            clarities: e.clarities,
            weight_cts: e.weightCts ? Number(e.weightCts) : null,
            count: e.count ? Number(e.count) : null,
            size: e.size || null,
            stone_code: e.stoneCode || null,
            serial_no: e.serialNo ?? null,
            is_weight_locked: !!e.isWeightLocked,
            weight_overridden: !!e.weightOverridden,
            erp_source: e.erpSource || null,
            actionable: e.actionable !== false,
            is_silent: !!e.isSilent,
            is_diamond: e.isDiamond !== false,
            completed: !!e.completed,
          })),
          total_weight_cts: totalWeight || null,
          diamond_count: totalPieces || null,
          gross_weight: grossWt ? Number(grossWt) : null,
          data_source: dataSource,
          erp_tag_meta: erpTagMeta || null,
        },
        tds: { colors: tdsColors, clarities: tdsClarities },
        lab_proposals: proposals,
        photos: photos.map(p => ({ url: p.url, is_primary: !!p.is_primary })),
        diverted_pills: diamondEntries
          .filter(e => e.divertedToCsv && e.serialNo != null)
          .map(e => Number(e.serialNo)),
        parent_dv_id: isRevision ? tag.id : undefined,
        sell_mode: sellMode || undefined,
      };
      await axios.post(`${API}/api/diamond-verification/submit`, payload,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success(
        sellMode === 'tds_interim'
          ? 'Saved — sellable at TDS while lab is pending.'
          : isRevision ? 'Diamond Verification revised.' : 'Diamond Verification saved.'
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [tagNumber, jewelleryRef, diamondEntries, grossWt, dataSource,
      tdsColors, tdsClarities, proposals, photos, totalWeight, totalPieces,
      erpTagMeta, onClose, onSaved, isRevision, tag]);

  const handleNext = () => {
    if (step === 1 && tagNumber && erpFetching) {
      alert('Please wait — verifying tag with the ERP…');
      return;
    }
    if (step === 1 && tagRejected) return;
    if (step === 1 && tagNotAtHo) return;
    if (step === 1 && noDiamondTag) return;  // user must use Send-to-CSV button
    if (step === 1 && tagNumber && tagNumber !== lastFetchedTag) {
      fetchErpPills(tagNumber);
      return;   // wait for fetch to resolve before advancing
    }
    if (step === 3 && !step3Valid) {
      alert('Pick at least one Visible Colour and one Visible Clarity before continuing.');
      return;
    }
    if (step === 4 && !step4Valid) {
      if (photos.length === 0) {
        alert('Please add at least one product photo before marking this DV complete.');
      } else if (!tdsProposal) {
        alert('A TDS lab proposal is mandatory — pick a Colour and Clarity for it.');
      } else if (incompleteProposals.length > 0) {
        alert(
          `These lab proposals are missing colour or clarity: ${incompleteProposals.map(p => p.lab).join(', ')}`
        );
      }
      return;
    }
    if (step < 4) {
      setStep(step + 1);
    } else {
      submitComplete();
    }
  };

  const handleBack = () => {
    if (step === 1) { onClose(); return; }
    setStep(s => s - 1);
  };

  // ── Lab helpers ──────────────────────────────────────────────────────────
  const addLab = (labName) => {
    if (proposals.find(p => p.lab === labName)) return;
    setProposals(prev => [...prev, { lab: labName, colors: [], clarities: [] }]);
    setAddingLab(false);
  };

  const removeLab = (labName) =>
    setProposals(prev => prev.filter(p => p.lab !== labName));

  const updateLab = (labName, field, val) =>
    setProposals(prev => prev.map(p => p.lab === labName ? { ...p, [field]: val } : p));

  const availableLabs = LABS.filter(l => !proposals.find(p => p.lab === l));

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50" data-testid="wizard">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 pt-safe flex items-center gap-3 py-3 shrink-0">
        <button onClick={handleBack} className="p-2 rounded-full hover:bg-emerald-600 active:bg-emerald-800">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="text-xs text-emerald-200 font-medium">Step {step} of 4</p>
          <p className="text-base font-bold">{STEP_TITLES[step]}</p>
        </div>
        {tagNumber && <span className="text-sm bg-emerald-600 px-2 py-1 rounded-lg font-mono">{tagNumber}</span>}
      </div>

      <StepDots current={step} />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 pb-32">

        {/* ── Step 1 ─────────────────────────────────────── */}
        {step === 1 && (
          <>
            <Section title="Tag Number">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Unique Tag Number
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={tagNumber}
                  onChange={e => handleTagNumberChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && tagNumber && !erpFetching) {
                      e.preventDefault();
                      fetchErpPills(tagNumber);
                    }
                  }}
                  placeholder="Auto-generated if empty"
                  className="w-full min-w-0 px-4 py-3 border border-gray-200 rounded-xl text-lg font-bold text-gray-900 bg-gray-50 focus:outline-none focus:border-emerald-500 focus:bg-white"
                  data-testid="tag-number-input"
                />
                <button
                  type="button"
                  onClick={() => fetchErpPills(tagNumber)}
                  disabled={!tagNumber || erpFetching || tagNumber === lastFetchedTag}
                  data-testid="get-data-btn"
                  className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold active:bg-emerald-700 disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {erpFetching ? 'Fetching…' : tagNumber === lastFetchedTag && erpTagMeta ? '✓ Loaded from ERP' : '⬇ Get Data from ERP'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {tagNumber
                  ? <>Tap <b>Get Data</b> (or press Enter) to verify the tag and pre-fill from ERP.</>
                  : 'Leave blank to auto-assign'}
              </p>
            </Section>

            {/* ── ERP fetch status / summary ── */}
            {erpFetching && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-3 mb-3 flex items-center gap-3" data-testid="erp-fetching-step1">
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <div>
                  <p className="text-xs font-bold text-indigo-800">Fetching from ERP…</p>
                  <p className="text-[11px] text-indigo-600">Verifying tag {tagNumber} is currently at HO.</p>
                </div>
              </div>
            )}
            {erpError && !erpFetching && tagNumber && (
              <div
                className={`border rounded-2xl p-3 mb-3 ${tagRejected ? 'bg-rose-50 border-rose-300' : 'bg-amber-50 border-amber-200'}`}
                data-testid="erp-error-step1"
              >
                <p className={`text-xs font-bold ${tagRejected ? 'text-rose-800' : 'text-amber-800'}`}>{erpError}</p>
                {tagRejected ? (
                  <p className="text-[11px] text-rose-700 mt-1 font-semibold">
                    This tag cannot proceed in DV — please update the ERP entry or the stone-code ruleset first.
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-700 mt-1">You can fill the fields manually below.</p>
                )}
              </div>
            )}
            {erpTagMeta && !erpFetching && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-3 mb-3" data-testid="erp-summary-step1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-indigo-800">✅ ERP Tag {erpTagMeta.tag_id}</p>
                  <span className="text-[10px] text-indigo-600 font-mono uppercase">{erpTagMeta.source}</span>
                </div>
                {erpTagMeta.style_name && (
                  <p className="text-[12px] text-indigo-900 font-semibold leading-snug">
                    Style: <span className="font-mono">{erpTagMeta.style_name}</span>
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="bg-white rounded-lg py-1.5 text-center">
                    <p className="text-sm font-bold text-indigo-700">{Number(erpTagMeta.gross_wt || 0).toFixed(3)}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Gross (g)</p>
                  </div>
                  <div className="bg-white rounded-lg py-1.5 text-center">
                    <p className="text-sm font-bold text-indigo-700">{Number(erpTagMeta.net_wt || 0).toFixed(3)}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Net (g)</p>
                  </div>
                  <div className="bg-white rounded-lg py-1.5 text-center">
                    <p className="text-sm font-bold text-indigo-700">{Number(erpTagMeta.stone_wt || 0).toFixed(3)}</p>
                    <p className="text-[9px] text-gray-500 uppercase">Stone (g)</p>
                  </div>
                </div>
                {erpTagMeta.certificate_code && (
                  <p className="text-[10px] text-indigo-600 mt-2 font-mono">Cert code (raw): {erpTagMeta.certificate_code}</p>
                )}
              </div>
            )}

            {/* ── Option C banner: zero diamonds → offer direct CSV handoff ── */}
            {noDiamondTag && (
              <div
                className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-3"
                data-testid="no-diamond-banner"
              >
                <div className="flex items-start gap-3 mb-3">
                  <Gem className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-amber-900">
                      {diamondEntries.length === 0 ? 'No stones on this tag' : 'No diamonds on this tag'}
                    </p>
                    <p className="text-[12px] text-amber-800 leading-snug mt-0.5">
                      {diamondEntries.length === 0
                        ? `Tag ${tagNumber} has no stones at all — Diamond Verification doesn't apply. Send it to Colour Stone Verification to close the loop.`
                        : `All ${diamondEntries.length} stone(s) on tag ${tagNumber} are colour stones. Diamond Verification can't price these — send the tag straight to Colour Stone Verification instead.`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={deflectToCsv}
                  disabled={deflecting}
                  data-testid="deflect-to-csv-btn"
                  className="w-full py-3 rounded-xl bg-amber-600 text-white text-sm font-bold active:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deflecting ? 'Sending…' : '→ Send to Colour Stone Verification'}
                </button>
              </div>
            )}

            <Section title="Jewellery Reference">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Product Type (from masters)
                {step1Prefilled && jewelleryRef && erpTagMeta?.style_name &&
                 jewelleryRef === matchProductOption(erpTagMeta.style_name) && (
                  <span className="ml-2 text-[10px] font-bold text-indigo-600 normal-case" data-testid="product-prefilled-badge">
                    ✓ ERP match
                  </span>
                )}
              </label>
              <div className="relative">
                <select
                  value={jewelleryRef}
                  onChange={e => setJewelleryRef(e.target.value)}
                  data-testid="jewellery-ref-input"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base text-gray-900 bg-gray-50 focus:outline-none focus:border-emerald-500 focus:bg-white appearance-none pr-10"
                >
                  <option value="">— Select product type —</option>
                  {productOptions.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
              </div>
              {step1Prefilled && erpTagMeta?.style_name && (
                <p className="text-[11px] text-indigo-600 mt-1.5" data-testid="erp-style-hint">
                  ERP style: <span className="font-mono">{erpTagMeta.style_name}</span>
                  {!jewelleryRef && <span className="text-amber-600 ml-1">(no catalog match — pick manually)</span>}
                </p>
              )}
            </Section>

            <Section title="Jewellery Weight">
              <NumInput
                label={<>Gross Weight (grams){step1Prefilled && grossWt && erpTagMeta?.gross_wt &&
                  String(erpTagMeta.gross_wt) === String(grossWt) && (
                  <span className="ml-2 text-[10px] font-bold text-indigo-600 normal-case" data-testid="gross-prefilled-badge">✓ ERP</span>
                )}</>}
                value={grossWt} onChange={setGrossWt} placeholder="0.00" decimal testid="gross-weight-input"
              />
            </Section>
          </>
        )}

        {/* ── Step 2 ─────────────────────────────────────── */}
        {step === 2 && (
          <Step2PillFlow
            tagNumber={tagNumber}
            erpFetching={erpFetching}
            erpError={erpError}
            erpTagMeta={erpTagMeta}
            entries={diamondEntries}
            setEntries={setDiamondEntries}
            activePillIdx={activePillIdx}
            setActivePillIdx={setActivePillIdx}
            fetchErpPills={fetchErpPills}
            dataSource={dataSource}
            setDataSource={setDataSource}
            totalWeight={totalWeight}
            totalPieces={totalPieces}
            onDivertToCsv={divertPillToCsv}
            onBulkDivertToCsv={bulkDivertPillsToCsv}
          />
        )}

        {/* ── Step 3 ─────────────────────────────────────── */}
        {step === 3 && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-3 flex gap-2 items-start">
              <span className="text-amber-500 text-base mt-0.5">⚠</span>
              <p className="text-xs text-amber-700 leading-relaxed">
                TDS visibility may differ from actual inserted values. Select what is <strong>visible</strong> to the naked eye.
              </p>
            </div>
            <Section>
              <ColorPicker
                selected={tdsColors}
                onChange={setTdsColors}
                label={tdsColors.length === 0 ? 'Visible Colour — required' : 'Visible Colour'}
                disabledBeyond={worstActualColour}
              />
              <ClarityPicker
                selected={tdsClarities}
                onChange={setTdsClarities}
                label={tdsClarities.length === 0 ? 'Visible Clarity — required' : 'Visible Clarity'}
                disabledBeyond={worstActualClarity}
              />
            </Section>
            {/* Diff hint */}
            {diamondEntries.length > 0 && (() => {
              const allActColors = [...new Set(diamondEntries.flatMap(e => e.colors))].sort();
              const allActClarities = [...new Set(diamondEntries.flatMap(e => e.clarities))].sort();
              return (
                <Section title="Actual vs Visible">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-400 font-semibold mb-1">ACTUAL COLOUR</p>
                      <p className="text-gray-800 font-mono">{allActColors.join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-semibold mb-1">VISIBLE COLOUR</p>
                      <p className={`font-mono ${JSON.stringify(allActColors) !== JSON.stringify([...tdsColors].sort()) ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>
                        {tdsColors.join(', ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-semibold mb-1">ACTUAL CLARITY</p>
                      <p className="text-gray-800 font-mono">{allActClarities.join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-semibold mb-1">VISIBLE CLARITY</p>
                      <p className={`font-mono ${JSON.stringify(allActClarities) !== JSON.stringify([...tdsClarities].sort()) ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>
                        {tdsClarities.join(', ') || '—'}
                      </p>
                    </div>
                  </div>
                </Section>
              );
            })()}
          </>
        )}

        {/* ── Step 4 ─────────────────────────────────────── */}
        {step === 4 && (
          <>
            <PhotoSection
              photos={photos}
              setPhotos={setPhotos}
            />
            {proposals.map(p => {
              const colorMissing   = p.colors.length === 0;
              const clarityMissing = p.clarities.length === 0;
              const incomplete = colorMissing || clarityMissing;
              const isTds = p.lab === 'TDS';
              return (
              <div
                key={p.lab}
                data-testid={`proposal-card-${p.lab}`}
                className={`bg-white rounded-2xl shadow-sm border-2 mb-3 overflow-hidden ${
                  incomplete ? 'border-amber-400' : 'border-gray-100'
                }`}
              >
                <div className={`flex items-center justify-between px-4 py-3 ${LAB_COLORS[p.lab]} text-white`}>
                  <div className="flex items-center gap-2">
                    <FlaskConical className="w-4 h-4" />
                    <span className="font-bold text-base">{p.lab}</span>
                    {isTds && (
                      <span className="text-[10px] bg-white/25 text-white px-1.5 py-0.5 rounded-full font-bold">REQUIRED</span>
                    )}
                    {incomplete && (
                      <span className="text-[10px] bg-amber-300/90 text-amber-900 px-1.5 py-0.5 rounded-full font-bold" data-testid={`proposal-${p.lab}-incomplete`}>
                        Needs {[colorMissing && 'colour', clarityMissing && 'clarity'].filter(Boolean).join(' & ')}
                      </span>
                    )}
                  </div>
                  {!isTds && (
                    <button onClick={() => removeLab(p.lab)} className="p-1 rounded-full hover:bg-white/20" data-testid={`remove-lab-${p.lab}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="p-4">
                  <ColorPicker
                    selected={p.colors}
                    onChange={v => updateLab(p.lab, 'colors', v)}
                    label={colorMissing ? 'Proposed Colour — required' : 'Proposed Colour'}
                    disabledBeyond={worstActualColour}
                  />
                  <ClarityPicker
                    selected={p.clarities}
                    onChange={v => updateLab(p.lab, 'clarities', v)}
                    label={clarityMissing ? 'Proposed Clarity — required' : 'Proposed Clarity'}
                    disabledBeyond={worstActualClarity}
                  />
                </div>
              </div>
              );
            })}

            {/* Add lab */}
            {availableLabs.length > 0 && (
              addingLab ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Select Lab</p>
                  <div className="grid grid-cols-2 gap-2">
                    {availableLabs.map(l => (
                      <button
                        key={l}
                        onClick={() => addLab(l)}
                        data-testid={`add-lab-${l}`}
                        className={`py-3 rounded-xl text-white font-bold text-sm ${LAB_COLORS[l]} active:opacity-80`}
                      >{l}</button>
                    ))}
                  </div>
                  <button onClick={() => setAddingLab(false)} className="mt-2 w-full text-sm text-gray-400 py-1">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingLab(true)}
                  data-testid="add-lab-btn"
                  className="w-full border-2 border-dashed border-gray-300 rounded-2xl py-4 text-sm text-gray-500 font-semibold flex items-center justify-center gap-2 hover:border-emerald-400 hover:text-emerald-600 active:bg-emerald-50 mb-3"
                >
                  <Plus className="w-4 h-4" /> Add Lab
                </button>
              )
            )}

            {proposals.length === 0 && !addingLab && (
              <div className="text-center py-8 text-gray-400">
                <FlaskConical className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No lab proposals yet</p>
                <p className="text-xs">Tap Add Lab to begin</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky bottom actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 pb-safe py-3 flex flex-col gap-2">
        {/* Submit Later — Sell at TDS (only when a lab batch is proposed) */}
        {step === 4 && hasLabBatch && !isRevision && (
          <button
            onClick={() => submitComplete('tds_interim')}
            disabled={saving || !step4Valid}
            data-testid="wizard-submit-later-btn"
            className="w-full py-3 rounded-2xl border-2 border-sky-500 text-sky-700 font-bold text-sm disabled:opacity-50 active:bg-sky-50 flex items-center justify-center gap-2"
          >
            <FlaskConical className="w-4 h-4" />
            {saving ? 'Saving…' : 'Submit Later — Sell at TDS (lab pending)'}
          </button>
        )}
        <div className="flex gap-3">
        {step > 1 && (
          <button
            onClick={() => setStep(s => s - 1)}
            className="flex-1 py-3.5 rounded-2xl border-2 border-gray-200 text-gray-700 font-bold text-sm"
          >
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={
            saving
            || (step === 1 && (noDiamondTag || tagRejected || tagNotAtHo))
            || (step === 2 && !step2AllDone)
            || (step === 3 && !step3Valid)
            || (step === 4 && !step4Valid)
          }
          data-testid="wizard-next-btn"
          className="flex-2 flex-1 py-3.5 rounded-2xl bg-emerald-600 text-white font-bold text-sm disabled:opacity-50 active:bg-emerald-700"
          style={{ flexGrow: 2 }}
        >
          {saving
            ? 'Saving…'
            : step === 1 && tagRejected
              ? 'Tag rejected — fix ERP / ruleset'
              : step === 1 && tagNotAtHo
                ? 'Tag not at HO — cannot proceed'
                : step === 1 && noDiamondTag
                  ? 'Use Send to CSV above ↑'
                  : step === 2 && !step2AllDone
                    ? `Verify all ${step2ActionableTotal} stones (${step2ActionableTotal - step2ActionableDone} left)`
                    : step === 3 && !step3Valid
                      ? 'Pick Visible Colour & Clarity'
                      : step === 4
                        ? step4MissingLabel
                        : 'Save & Next →'}
        </button>
        </div>
      </div>
    </div>
  );
};
